import {
  MutationOptions,
  QueryObserverOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import {
  deleteAttachmentApi,
  getAttachmentApi,
  getAttachmentFromSignedUrlApi,
  getAttachmentTypesApi,
  getAttachmentUrlApi,
  getViewAttachmentApi,
} from 'api/attachments';
import {
  DeleteAttachmentRequestParamsDTO,
  GetAttachmentFromSignedUrlResponseDTO,
  GetAttachmentResponseDTO,
  GetAttachmentUrlResponseDTO,
} from 'declarations/dto/attachments';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { AttachmentTypeI } from 'declarations/interfaces/attachments';
import { ApiErrorI } from 'declarations/interfaces/errors';

type UseDeleteAttachmentParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, DeleteAttachmentRequestParamsDTO>['onSuccess'];
};

type UseFetchAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentResponseDTO, ApiErrorI>['onSuccess'];
};

type UseFetchViewAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentResponseDTO, ApiErrorI>['onSuccess'];
};

type UseGetAttachmentUrlParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentUrlResponseDTO, ApiErrorI>['onSuccess'];
};

type UseDownloadAttachmentFromSignedUrlParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentFromSignedUrlResponseDTO, ApiErrorI>['onSuccess'];
};

export const useFetchAttachmentForDownload = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseFetchAttachmentParamsT) =>
  useQuery<GetAttachmentResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchAttachment, { ticketId, fileName }],
    () => getAttachmentApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useFetchAttachmentForPreview = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseFetchViewAttachmentParamsT) =>
  useQuery<GetAttachmentResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchViewAttachment, { ticketId, fileName }],
    () => getViewAttachmentApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useDeleteAttachment = ({ onSuccess }: UseDeleteAttachmentParamsT) =>
  useMutation<void, ApiErrorI, DeleteAttachmentRequestParamsDTO>(
    [RequestQueryKeysE.DeleteAttachment],
    ({ ticketId, fileName, originalName }) => deleteAttachmentApi(ticketId, fileName, originalName),
    { onSuccess }
  );

export const useGetAttachmentUrl = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseGetAttachmentUrlParamsT) =>
  useQuery<GetAttachmentUrlResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.GetAttachmentUrl, { ticketId, fileName }],
    () => getAttachmentUrlApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useDownloadAttachmentFromSignedUrl = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseDownloadAttachmentFromSignedUrlParamsT) =>
  useQuery<GetAttachmentFromSignedUrlResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.GetAttachmentFromSignedUrl, { ticketId, fileName }],
    () => getAttachmentFromSignedUrlApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useGetAttachmentTypesApi = () =>
  useQuery<AttachmentTypeI[], ApiErrorI>([RequestQueryKeysE.GetAttachmentTypes], () =>
    getAttachmentTypesApi()
  );
